import React, { useState } from 'react';
import { AssessmentQuestion } from '../Constants/DASS21_QUESTIONS';

interface Answer {
  questionId: string;
  score: number;
}

interface AssessmentShellProps {
  assessmentType: string;
  questions: AssessmentQuestion[];
  scaleLabels: string[];
  onSubmit: (answers: Answer[]) => Promise<void>;
  companyName?: string;
}

export const AssessmentShell: React.FC<AssessmentShellProps> = ({
  assessmentType,
  questions,
  scaleLabels,
  onSubmit,
  companyName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = questions.length;
  const question = questions[currentIndex];
  const progress = Math.round(((currentIndex + (answers[question?.id] !== undefined ? 1 : 0)) / total) * 100);
  const currentAnswer = question ? answers[question.id] : undefined;

  const handleAnswer = (score: number) => {
    setAnswers((prev) => ({ ...prev, [question.id]: score }));
  };

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex((i) => i + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload: Answer[] = questions.map((q) => ({
        questionId: q.id,
        score: answers[q.id] ?? 0,
      }));
      await onSubmit(payload);
      setSubmitted(true);
    } catch {
      // error handled in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const allAnswered = questions.every((q) => answers[q.id] !== undefined);
  const isLast = currentIndex === total - 1;

  if (submitted) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4'>
        <div className='max-w-md text-center'>
          <div className='mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100'>
            <svg className='h-8 w-8 text-emerald-600' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </div>
          <h2 className='mb-2 text-xl font-extrabold text-slate-900'>Thank you!</h2>
          <p className='text-sm text-slate-600'>
            Your responses have been submitted successfully. You may close this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen flex-col bg-slate-50'>

      { /* Branded header */ }
      <header className='border-b border-slate-200 bg-white px-4 py-3 shadow-sm'>
        <div className='mx-auto flex max-w-2xl items-center justify-between'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-widest text-emerald-600'>
              { companyName ?? 'Wellness Screening' }
            </p>
            <h1 className='text-sm font-extrabold text-slate-900'>{ assessmentType } Assessment</h1>
          </div>
          <span className='text-xs font-semibold text-slate-400'>
            { currentIndex + 1 } / { total }
          </span>
        </div>
      </header>

      { /* Progress bar */ }
      <div className='h-1.5 w-full bg-slate-100'>
        <div
            className='h-1.5 bg-emerald-500 transition-all duration-300'
            style={{ width: `${progress}%` }}
        />
      </div>

      { /* Question card */ }
      <main className='mx-auto mt-8 w-full max-w-2xl flex-1 px-4 pb-12'>
        <div className='rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400'>
            Question { currentIndex + 1 } of { total }
          </p>
          <h2 className='mb-6 text-xl font-semibold text-slate-900 leading-snug'>
            { question?.text }
          </h2>

          { /* Response options — 0-3 scale */ }
          <div className='grid gap-3'>
            { scaleLabels.map((label, score) => {
              const selected = currentAnswer === score;
              return (
                <button
                    key={score}
                    type='button'
                    onClick={() => handleAnswer(score)}
                    className={`flex min-h-[52px] w-full items-center gap-4 rounded-xl border-2 px-5 py-3 text-left transition-all ${
                      selected
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
                    selected
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : 'border-slate-300 bg-white text-slate-500'
                  }`}>
                    { score }
                  </span>
                  <span className={`text-sm font-medium ${selected ? 'text-emerald-800' : 'text-slate-700'}`}>
                    { label }
                  </span>
                </button>
              );
            }) }
          </div>
        </div>

        { /* Navigation */ }
        <div className='mt-6 flex items-center justify-between'>
          <button
              type='button'
              onClick={handleBack}
              disabled={currentIndex === 0}
              className='flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 19.5L8.25 12l7.5-7.5' />
            </svg>
            Back
          </button>

          { isLast ? (
            <button
                type='button'
                onClick={handleSubmit}
                disabled={!allAnswered || isSubmitting}
                className='inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors'
            >
              { isSubmitting ? (
                <>
                  <svg className='h-4 w-4 animate-spin' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                  </svg>
                  Submitting…
                </>
              ) : 'Submit' }
            </button>
          ) : (
            <button
                type='button'
                onClick={handleNext}
                disabled={currentAnswer === undefined}
                className='inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors'
            >
              Next
              <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                <path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
              </svg>
            </button>
          ) }
        </div>
      </main>
    </div>
  );
};
